import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtTime, BACKEND } from "@/lib/api";
import { usePoll, Panel, Pill, CopyButton, Stat } from "@/components/common";
import { fmtBytes } from "@/lib/api";
import { ServerForm } from "@/components/ServerForm";
import { UsageCharts } from "@/components/UsageCharts";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const FORMATS = [["hls", "HLS"], ["ts", "MPEG-TS"], ["status", "Status"], ["rtmp_pull", "RTMP pull"]];

function UrlList({ s }) {
  const [fmt, setFmt] = useState("hls");
  const all = (s.urls || []).map((u) => u[fmt]).join("\n");
  return (
    <Panel title={`Output URLs · ${s.urls.length} granted channel(s)`} testid="server-url-list"
      right={<div className="flex items-center gap-1">
        <CopyButton text={all} testid="copy-all-urls-btn" label="Copy all" />
        <a data-testid="export-m3u-hls-btn" href={`${BACKEND}/api/admin/servers/${s.id}/export?fmt=m3u&kind=hls`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-400 hover:text-cyan-300 hover:bg-slate-800"><Download size={12} /> M3U (HLS)</a>
        <a data-testid="export-m3u-ts-btn" href={`${BACKEND}/api/admin/servers/${s.id}/export?fmt=m3u&kind=ts`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-400 hover:text-cyan-300 hover:bg-slate-800"><Download size={12} /> M3U (TS)</a>
        <a data-testid="export-csv-btn" href={`${BACKEND}/api/admin/servers/${s.id}/export?fmt=csv`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-400 hover:text-cyan-300 hover:bg-slate-800"><Download size={12} /> CSV</a>
      </div>}>
      <Tabs value={fmt} onValueChange={setFmt} className="mb-3">
        <TabsList className="bg-[#0B111B] border border-slate-800">
          {FORMATS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`url-format-${k}`} className="text-xs">{l}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      {fmt === "rtmp_pull" && <p className="text-[11px] text-amber-400 mb-2">RTMP pull is available once the origin runs on a dedicated host (Phase 3). Use HLS, MPEG-TS or RTMP push today.</p>}
      {s.urls.length === 0 && <p className="text-sm text-slate-500">No channels granted yet — edit the server to grant channels.</p>}
      <div className="divide-y divide-slate-800/70">
        {s.urls.map((u) => (
          <div key={u.slug} data-testid={`url-row-${u.slug}`} className="flex items-center gap-3 py-2">
            <div className="w-44 shrink-0"><div className="text-sm text-slate-200 truncate">{u.name}</div><div className="font-mono text-[11px] text-slate-500">{u.slug}</div></div>
            <code className="flex-1 truncate text-xs text-cyan-300" data-testid={`url-${u.slug}`}>{u[fmt]}</code>
            <CopyButton text={u[fmt]} testid={`copy-url-${u.slug}`} />
          </div>
        ))}
      </div>
    </Panel>
  );
}

export default function ServerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: s, reload } = usePoll(`/admin/servers/${id}`, 5000);
  const { data: log } = usePoll(`/admin/access-log?server_id=${id}&limit=50`, 5000);
  const [edit, setEdit] = useState(false);
  if (!s) return <div className="text-slate-500 font-mono text-sm">loading…</div>;

  const setEnabled = async (v) => {
    try { await api.put(`/admin/servers/${id}`, { ...s, enabled: v }); toast.success(v ? "Server enabled" : "Server disabled — requests now refused"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => { if (!window.confirm(`Remove ${s.name}?`)) return; await api.delete(`/admin/servers/${id}`); nav("/servers"); };

  return (
    <div className="space-y-4">
      <button onClick={() => nav("/servers")} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"><ArrowLeft size={13} /> Authorized servers</button>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-100" data-testid="server-title">{s.name}</h1>
          <div className="flex flex-wrap gap-1.5 mt-2">{s.ips.map((ip) => <Pill key={ip} tone="cyan">{ip}</Pill>)}{s.all_channels && <Pill tone="blue">all channels</Pill>}</div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-300"><Switch data-testid="server-detail-enabled-switch" checked={s.enabled} onCheckedChange={setEnabled} /> {s.enabled ? "Enabled" : "Disabled"}</label>
          <Button data-testid="server-edit-btn" variant="outline" size="sm" className="border-slate-700" onClick={() => setEdit(true)}><Pencil size={13} /> Edit grants</Button>
          <Button data-testid="server-delete-btn" variant="outline" size="sm" className="border-red-900 text-red-400" onClick={del}><Trash2 size={13} /></Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="Connections" value={`${s.usage?.connections ?? 0} / ${s.max_connections || "∞"}`} testid="server-usage-conns" tone={s.max_connections && s.usage?.connections >= s.max_connections ? "text-red-400" : undefined} />
        <Stat label="Bandwidth (10 s)" value={`${(s.usage?.mbps ?? 0).toFixed(2)} / ${s.max_mbps || "∞"} Mb/s`} testid="server-usage-mbps" tone={s.max_mbps && s.usage?.mbps >= s.max_mbps ? "text-red-400" : undefined} />
        <Stat label="Served since boot" value={fmtBytes(s.usage?.bytes_total ?? 0)} />
        <Stat label="Granted" value={s.all_channels ? "all channels" : `${s.channels.length} channel(s)`} />
        <Stat label="Alert webhook" value={s.webhook_url ? "set" : "none"} testid="server-webhook-state" />
      </div>
      <UsageCharts kind="server" refId={id} testid="server-usage" charts={[
        { title: "Bandwidth (Mb/s)", series: [["mbps", "#06B6D4", "Mb/s"]], fmt: (v) => `${Number(v).toFixed(2)}`, cap: s.max_mbps, capLabel: `cap ${s.max_mbps} Mb/s` },
        { title: "Connections", series: [["connections", "#3B82F6", "connections"]], fmt: (v) => `${Number(v).toFixed(1)}`, cap: s.max_connections, capLabel: `cap ${s.max_connections}` },
      ]} />
      <UrlList s={s} />
      <div className="grid xl:grid-cols-2 gap-4">
        <Panel title="Pulling now" testid="server-pullers">
          {s.pullers.length === 0 ? <p className="text-sm text-slate-500">Nothing pulling right now.</p> : s.pullers.map((p, i) => (
            <div key={i} className="flex gap-3 py-1.5 text-xs font-mono border-b border-slate-800/60"><span className="text-slate-300">{p.ip}</span><span className="text-cyan-300">{p.slug}</span><Pill>{p.format}</Pill><span className="text-slate-500">idle {p.idle}s</span></div>
          ))}
        </Panel>
        <Panel title="Recent access">
          <div className="max-h-72 overflow-y-auto">
            {(log || []).map((l) => (
              <div key={l.id} className="flex gap-3 py-1.5 text-xs font-mono border-b border-slate-800/60">
                <span className="text-slate-500">{fmtTime(l.ts)}</span><span className="text-slate-300">{l.ip}</span><span className="text-cyan-300">{l.slug}</span><Pill>{l.format}</Pill>
                <Pill tone={l.allowed ? "green" : "red"}>{l.allowed ? "allowed" : l.reason}</Pill>
              </div>
            ))}
            {log && log.length === 0 && <p className="text-sm text-slate-500">No requests yet.</p>}
          </div>
        </Panel>
      </div>
      <ServerForm open={edit} onOpenChange={setEdit} initial={s} onSaved={reload} />
    </div>
  );
}
